import { useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Configuration, SkusApi } from '@/api';
import useGetSupplierQuery from '@/api/queries/useGetSupplierQuery';
import CustomToast from '@/components/CustomToast';
import { ProgressCallback } from '@/components/ImageUploadForm';
import { Image } from '@/models/Image';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import ProductForm from '@/modules/product/components/ProductForm';
import { SkuCreate, SkuUpdate } from '@/modules/product/models/Sku';
import { ProductListPath } from '@/modules/product/paths';
import SettingsHeader from '@/modules/settings/components/SettingsHeader';
import { CubeIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const ProductAdd = () => {
  const [draftSkuId] = useState<string>(() => uuid());

  const navigate = useNavigate();

  const { currentUser } = useAuth();

  const { supplierId, storeId } = currentUser();

  const { supplier, isLoading } = useGetSupplierQuery(supplierId);

  const skusApi = new SkusApi(new Configuration(useAuth));

  const handleSubmit = async (sku: SkuCreate | SkuUpdate) => {
    try {
      const newSku = await skusApi.createSku(sku);
      if (!newSku) {
        toast.custom((t) => (
          <CustomToast
            id={t.id}
            message={'Failed to create product. Please try again.'}
            visible={t.visible}
            variant="error"
          />
        ));
        return;
      }

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Product "${newSku.name}" created successfully!`}
          visible={t.visible}
        />
      ));

      navigate(ProductListPath);
    } catch (error) {
      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={'Failed to create product. Please try again.'}
          visible={t.visible}
          variant="error"
        />
      ));
      console.error('Error while creating product:', error);
    }
  };

  if (isLoading) {
    return;
  }

  const handleUploadImage = (
    file: File,
    index: number,
    callback: ProgressCallback,
  ): Promise<Image> => {
    return skusApi.uploadSkuImage(draftSkuId, file, index + 1, callback);
  };

  const handleDeleteImage = (index: number): Promise<void> => {
    return skusApi.deleteSkuImage(draftSkuId, index + 1);
  };

  // Show the supplier select field, when the user is associated to a store (retailer)
  const showSupplierSelect = !!storeId;
  // Show the supplier text, when the user is not a supplier (retailer)
  const showSupplier = !supplierId && !showSupplierSelect;

  return (
    <>
      <SettingsHeader title="Add Product" icon={<CubeIcon className="w-6 h-6" />} />

      <ProductForm
        skuId={draftSkuId}
        onSubmit={handleSubmit}
        onUploadImage={handleUploadImage}
        onDeleteImage={handleDeleteImage}
        supplier={supplier}
        showSupplier={showSupplier}
        showSupplierSelect={showSupplierSelect}
        selectSupplierCreatedByStoreId={storeId}
      />
    </>
  );
};

export default ProductAdd;
